import { useAtom, useAtomValue } from 'jotai';
import * as React from 'react';
import { useState } from 'react';
import { Image } from 'react-native';
import {
    NestableDraggableFlatList,
    type RenderItemParams,
} from 'react-native-draggable-flatlist';
import { BorderlessButton } from 'react-native-gesture-handler';
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import { CircleAlertIcon, Icon, View, XIcon, XStack, YStack } from 'smoothie';
import { v4 as uuidv4 } from 'uuid';

import ArrowShort from '../../../../../assets/images/banking/paycheck-routing/arrow-short.png';
import Line from '../../../../../assets/images/banking/paycheck-routing/line.png';
import LineShort from '../../../../../assets/images/banking/paycheck-routing/line-short.png';
import useGetAllBankAccountDetails from '../../../hooks/useGetAllBankAccountDetails';
import { currentlyEditingConfigAtom } from '../../../state/PaycheckRoutingAtoms';
import { bankAccountDisplayDetails } from '../../../state/SpacesAtoms';
import { type BankAccountDisplayDetail } from '../../../types/BankAccount';
import type { PaycheckRoutingRule } from '../../../types/PaycheckRouting';
import AddRuleButton from '../common/AddRuleButton';
import RuleSummary from '../common/RuleSummary';
import { isRoutingRuleBroken } from '../helpers';

interface SortableRulesListProps {
    setSelectedRule: (rule: Partial<PaycheckRoutingRule>) => void;
    isAddingOrEditingRule: boolean;
    isEditingRemainingRules: boolean;
    setIsAddingRule: (isAddingRule: boolean) => void;
    setIsEditingRule: (isEditingRule: boolean) => void;
    closedAccountsInConfig: BankAccountDisplayDetail[];
}

const SortableRulesList = ({
    setSelectedRule,
    isAddingOrEditingRule,
    isEditingRemainingRules,
    setIsAddingRule,
    setIsEditingRule,
    closedAccountsInConfig,
}: SortableRulesListProps) => {
    const [isSortingRules, setIsSortingRules] = useState(false);

    const [currentlyEditingConfig, setCurrentlyEditingConfig] = useAtom(
        currentlyEditingConfigAtom,
    );

    const bankAccountDetails = useAtomValue(bankAccountDisplayDetails);

    const renderRule = ({
        item,
        drag,
    }: RenderItemParams<PaycheckRoutingRule>) => {
        const accountDetails = [
            ...bankAccountDetails,
            ...closedAccountsInConfig,
        ].find((detail) => detail.bankAccountId === item.accountId);
        const account = item.accountMetadata ?? accountDetails;

        if (!account) return null;

        const accountBelongsToPartner = !!item.accountMetadata;

        const { closedAccountDetails } = useGetAllBankAccountDetails(false);

        const scale = useSharedValue(1);

        const animatedStyle = useAnimatedStyle(() => ({
            transform: [
                {
                    scale: withTiming(scale.value, {
                        duration: 200,
                        easing: Easing.bezier(0.65, 0, 0.35, 1),
                    }),
                },
            ],
        }));

        const needsAttention = isRoutingRuleBroken({
            rule: item,
            closedAccountDetails,
        });
        // we need to use a combination of Animated.View and  BorderlessButton in order to make the different press callbacks,
        // styles, and drag and drop functionality all play nice with each other inside a ScrollView
        return (
            <Animated.View
                style={[
                    {
                        marginVertical: 10,
                    },
                    animatedStyle,
                ]}
            >
                <XStack justifyContent="space-between" alignItems="center">
                    <XStack alignItems="center" gap={14}>
                        {needsAttention && (
                            <Icon
                                icon={CircleAlertIcon}
                                color="$orange900"
                                size={18}
                            />
                        )}
                        <BorderlessButton
                            enabled={!needsAttention}
                            onActiveStateChange={(active) => {
                                active
                                    ? (scale.value = accountBelongsToPartner
                                          ? 1
                                          : 0.95)
                                    : (scale.value = 1);
                            }}
                            activeOpacity={1}
                            delayLongPress={200}
                            onLongPress={
                                isAddingOrEditingRule ||
                                isEditingRemainingRules ||
                                accountBelongsToPartner
                                    ? () => {}
                                    : drag
                            }
                            onPress={() => {
                                if (
                                    !accountBelongsToPartner &&
                                    !isAddingOrEditingRule &&
                                    !isEditingRemainingRules
                                ) {
                                    setIsEditingRule(true);
                                    setSelectedRule(item);
                                }
                            }}
                        >
                            <RuleSummary
                                key={item.id}
                                account={account}
                                rule={item}
                                needsAttention={needsAttention}
                            />
                        </BorderlessButton>
                    </XStack>
                    <View
                        onPress={
                            isSortingRules ||
                            isAddingOrEditingRule ||
                            isEditingRemainingRules
                                ? undefined
                                : () => {
                                      setCurrentlyEditingConfig({
                                          ...currentlyEditingConfig,
                                          rules: {
                                              primary: (
                                                  currentlyEditingConfig?.rules
                                                      ?.primary ?? []
                                              ).filter((r) => r.id !== item.id),
                                              remaining:
                                                  currentlyEditingConfig?.rules
                                                      ?.remaining ?? [],
                                          },
                                      });
                                  }
                        }
                    >
                        <Icon icon={XIcon} size={16} color="$gray900" />
                    </View>
                </XStack>
            </Animated.View>
        );
    };

    return (
        <XStack gap={8} width="100%">
            <YStack marginLeft={16}>
                <Image
                    source={LineShort}
                    style={{
                        height: 26,
                    }}
                />
                {Array.from({
                    length: (currentlyEditingConfig?.rules?.primary ?? [])
                        .length,
                }).map((_, i) => (
                    <View key={i}>
                        <Image
                            source={ArrowShort}
                            style={{
                                height: 46,
                                resizeMode: 'stretch',
                            }}
                        />
                        <Image
                            source={LineShort}
                            style={{
                                height: 26,
                            }}
                        />
                    </View>
                ))}
                <Image
                    source={isAddingOrEditingRule ? Line : ArrowShort}
                    style={{
                        height: isAddingOrEditingRule ? 42 : 46,
                        resizeMode: 'stretch',
                    }}
                />
            </YStack>
            <YStack width="100%" flexShrink={1} marginTop={36}>
                {/* this is a ScrollView that can handle a drag and drop list inside it */}
                <NestableDraggableFlatList
                    data={currentlyEditingConfig?.rules?.primary ?? []}
                    renderItem={renderRule}
                    keyExtractor={(item) => item.id}
                    onDragBegin={() => {
                        setIsSortingRules(true);
                    }}
                    onDragEnd={({ data }) => {
                        setCurrentlyEditingConfig({
                            ...currentlyEditingConfig,
                            rules: {
                                primary: data,
                                remaining:
                                    currentlyEditingConfig?.rules?.remaining ??
                                    [],
                            },
                        });
                        setIsSortingRules(false);
                    }}
                />
                {!isAddingOrEditingRule && !isEditingRemainingRules && (
                    <View height={68} justifyContent="center" marginBottom={16}>
                        <AddRuleButton
                            onPress={() => {
                                setSelectedRule({
                                    id: uuidv4(),
                                });
                                setIsAddingRule(true);
                            }}
                        />
                    </View>
                )}
            </YStack>
        </XStack>
    );
};

export default SortableRulesList;
